import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, output, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { routes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { FlexibleConnectedPositionStrategy, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { NavigationService, pathParameter } from '@libs/shared-services';
import {
  PerioExamCancelModalComponent,
  PerioExamCancelModalPayload,
} from '../../../perio-exam-cancel-modal/perio-exam-cancel-modal.component';
import {
  PerioExamHeaderMoreModalComponent,
  PerioExamHeaderMoreModalResult,
} from '../../../perio-exam-header-more-modal/perio-exam-header-more-modal.component';

@Component({
  selector: 'lib-perio-exam-header',
  imports: [SvgIconComponent, TranslatePipe],
  templateUrl: './perio-exam-header.component.html',
  styleUrl: './perio-exam-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioExamHeaderComponent implements OnDestroy {
  public isValid = input.required<boolean>();
  public examFormWidth = input.required<number>();
  public isEditing = input<boolean>();
  public cancelExam = output<void>();
  public resetExam = output<void>();
  public done = output<void>();

  protected readonly routes = routes;

  private readonly navigationService = inject(NavigationService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(Dialog);
  private readonly overlayPositionBuilder = inject(OverlayPositionBuilder);

  public moreModalRef: WritableSignal<DialogRef<PerioExamHeaderMoreModalResult> | null> = signal(null);

  public ngOnDestroy(): void {
    if (this.moreModalRef()) {
      this.moreModalRef()?.close();
    }
  }

  protected onCancelButtonClick(): void {
    this.dialog
      .open<PerioExamCancelModalPayload>(PerioExamCancelModalComponent, {
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      })
      .closed.subscribe((res) => {
        if (!res?.discard) {
          return;
        }
        this.cancelExam.emit();
      });
  }

  protected onDoneButtonClick(): void {
    this.done.emit();
  }

  protected onBackButtonClick(): void {
    this.navigationService.navigate('perioOnSiteForPatient', false, [
      this.route.snapshot.paramMap.get(pathParameter('siteId'))!,
      this.route.snapshot.paramMap.get(pathParameter('patientId'))!,
    ]);
  }

  protected onMoreButtonClick(event: MouseEvent): void {
    if (this.moreModalRef()) return;

    const target = event.target as HTMLElement;
    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlayPositionBuilder.flexibleConnectedTo(target).withPositions([
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top',
      },
    ]);

    this.moreModalRef.set(
      this.dialog.open<PerioExamHeaderMoreModalResult>(PerioExamHeaderMoreModalComponent, {
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        panelClass: ['[&>*]:outline-none'],
        positionStrategy: positionStrategy,
      }),
    );

    this.moreModalRef()?.closed.subscribe((result) => {
      if (result?.onResetClicked) {
        this.resetExam.emit();
      }
      this.moreModalRef.set(null);
    });
  }
}
